import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '../Patient.module.css';
import FilterPatientView from '../FilterPatientDetail';
import BloodPressureCardView from '../modality-charts/BloodPressureCardView';
import OxygenSaturationCardView from '../modality-charts/OxygenSaturationCardView';
import { useDispatch, useSelector } from 'react-redux';
import useAxios from '../../../services/AxiosConfig';
import TemperatureCardView from '../modality-charts/TemperatureCardView';
import RespirationCardView from '../modality-charts/RespirationCardView';
import ECGCardView from '../modality-charts/ECGCardView';
import WeightCardView from '../modality-charts/WeightCardView';
import AuscultationCardView from '../modality-charts/AuscultationCardView';
import GlucoseLevelCardView from '../modality-charts/GlucoseLevelCardView';
import { resetVitalState, setItem, setModality } from './VitalSlice';
import StepCardView from '../modality-charts/StepCardView';
import ECGHRVCardView from '../modality-charts/ECGHRVCardView';
import { toggleVitalsExpanded, toggleShowingTrue, setSelectedCharts, setAverageGrouped, setFilterItem } from '../Slices/PatientDetailFilterSlice';
import { ExpandIcon } from '../../../components/icons/Icons';
import { Modal } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { formatDateString, formatDateStringWithoutYear, parseCompletedDate } from '../../../utils/dateUtils';
import { convertTemperature, convertWeight } from '../../../utils/unitUtils';
import { startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import ChartDetailView from '../modality-charts/ChartDetailView';
import { useDeviceReadingsWebSocket } from '../../../hooks/useDeviceReadingsWebSocket';

const VitalsView = () => {
    const dispatch = useDispatch();
    const chartRefs = useRef({});
    const dateRangeSyncedForPatientRef = useRef(null);
    const {unitType, dateFormat} = useSelector((state) => state.userInfo);
    const [healthEvaluationData, setHealthEvaluationData] = useState([]);
    const [alertsForPeriod, setAlertsForPeriod] = useState([]);
    const [auscData, setAuscData] = useState([]);
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const { startDate, endDate, vitalsExpanded, selectedCharts, readingInterval, selectedScrollChart, averageGrouped } = useSelector((state) => state.patientDetailFilter);
    const patient = useSelector((state) => state.patient);
    const { id, carePrograms } = patient;
    const vitals = useSelector((state) => state.vital);
    
    // WebSocket subscription for device readings
    const accountIdsToMonitor = id ? [id] : [];
    const { readings: deviceReadings, lastUpdateTimestamp } = useDeviceReadingsWebSocket(accountIdsToMonitor);
    const [vitalChartOrder, setVitalChartOrder] = useState([
        'oxygenSaturation', 
        'ECG', 
        'ECGHRV', 
        'bloodPressure', 
        'temperature', 
        'respiratoryRate', 
        'weight', 
        'AUSC', 
        'steps', 
        'glucoseLevel'
    ]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState('');

    useEffect(() => {
        if (!selectedScrollChart) return;
    
        if (chartRefs.current[selectedScrollChart]?.current) {
            chartRefs.current[selectedScrollChart].current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedScrollChart, selectedCharts, vitals]);


    useEffect(() => {
        if (!id || dateRangeSyncedForPatientRef.current === id) return;

        const fetchLatestReadings = async () => {
            try {
                const response = await httpService.get(`device/readings/latestByIds?accountIds=${id}&calibrationReading=false`);
                if (response.data.data && response.data.data.length > 0) {
                    let readings = response.data.data[0].readings ?? [];
                    if (readings.length > 0) {
                        const mostRecentDate = readings.reduce((maxDateRaw, reading) => {
                            if (!reading.completedDate) return maxDateRaw;
                            const currentDate = parseCompletedDate(reading.completedDate);
                            if (!currentDate) return maxDateRaw;
                            if (!maxDateRaw) return reading.completedDate;
                            const maxDateObj = parseCompletedDate(maxDateRaw);
                            if (!maxDateObj) return reading.completedDate;
                            return currentDate > maxDateObj ? reading.completedDate : maxDateRaw;
                        }, null);

                        if (mostRecentDate) {
                            const latestDate = parseCompletedDate(mostRecentDate);
                            if (latestDate) {
                                const today = new Date();
                                const todayStart = startOfDay(today);
                                const todayEnd = endOfDay(today);

                                // Check if the latest reading is within today's range
                                if (!isWithinInterval(latestDate, { start: todayStart, end: todayEnd })) {
                                    const readingDayStart = startOfDay(latestDate);
                                    const readingDayEnd = endOfDay(latestDate);

                                    dispatch(setFilterItem({ key: "timePeriod", value: 'Custom' }));
                                    dispatch(setFilterItem({ key: "startDate", value: readingDayStart.toISOString() }));
                                    dispatch(setFilterItem({ key: "endDate", value: readingDayEnd.toISOString() }));
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                dateRangeSyncedForPatientRef.current = id;
            }
        };
        fetchLatestReadings();
    }, [id, dispatch, httpService]);

    const handleIntervalChange = (timeStamp) => {
        const intervalHours = 
        readingInterval === 'Day' ? 24 :
        readingInterval === '6Hour' ? 6 :
        readingInterval === '12Hour' ? 12 : 24;

        const intervalStartDate = new Date(timeStamp);
        intervalStartDate.setHours(intervalStartDate.getHours() + intervalHours);

        const formattedIntervalEndDate = intervalStartDate.toISOString();
        dispatch(setFilterItem({ key: "timePeriod", value: 'CustomPoint' }));
        dispatch(setFilterItem({ key: "startDate", value: timeStamp }));
        dispatch(setFilterItem({ key: "endDate", value: formattedIntervalEndDate }));
    };

    const handlePointClick = (deviceName, payload, vitals) => {
        const timeStamp = payload.timeStamp;
        if (averageGrouped && vitals.modalityType !== 'Audio') {
          handleIntervalChange(timeStamp);
        } else {
          setModalContent({ timeStamp, deviceName, modalityId: vitals.modalityId, vital: vitals.modalityName, modalityType: vitals.modalityType, patientId: id });
          setIsModalOpen(true);
        }
      };

    // Process readings array into graph format (extracted for reuse)
    const processReadingsIntoGraphFormat = useCallback((readingsArray, groupedByAvg = false) => {
        const filteredData = readingsArray.filter(reading => reading.modalityId !== 7);
        const auscData = readingsArray.filter(reading => reading.modalityId === 7);

        const groupedData = {};
        const sortedFilteredData = [...filteredData].sort((a, b) => {
            return new Date(a.completedDate) - new Date(b.completedDate);
        });

        sortedFilteredData.forEach(reading => {
            const dateString = reading.completedDate;
            const completedDate = new Date(dateString);
            const formattedDate = formatDateStringWithoutYear(completedDate.toISOString(), dateFormat);
            const hour = String(completedDate.getHours()).padStart(2, '0');
            const minute = String(completedDate.getMinutes()).padStart(2, '0');
            const second = String(completedDate.getSeconds()).padStart(2, '0');
            
            // groupKey includes seconds for deduplication, dateKey excludes seconds for display
            const groupKey = (groupedByAvg && readingInterval === 'Day') ? 
                formattedDate
                : `${formattedDate}-${hour}:${minute}:${second}`;
            const dateKey = (groupedByAvg && readingInterval === 'Day') ?
                formattedDate
                : `${formattedDate}-${hour}:${minute}`;

            const displayKey = formattedDate;

            if (!groupedData[groupKey]) {
                groupedData[groupKey] = {
                    displayKey,
                    dateKey,
                    timeStamp: dateString,
                    formattedDate,
                    sortTime: completedDate.getTime(),
                    data: []
                };
            }

            let deviceGroup = groupedData[groupKey].data.find(group => group.deviceId === reading.deviceId);
            if (!deviceGroup) {
                deviceGroup = {
                    deviceId: reading.deviceId,
                    deviceName: reading.model,
                    measurements: {
                        temperature: null,
                        heartRate: null,
                        oxygenSaturation: null,
                        ECG: null,
                        ECGHRV: null,
                        systolic: null,
                        respiratoryRate: null,
                        AUSC: null,
                        weight: null,
                        diastolic: null,
                        glucoseLevel: null,
                        steps: null,
                        posture: null,
                        activityLevel: null
                    }
                };
                groupedData[groupKey].data.push(deviceGroup);
            }
            let measurementVal = reading.measurement;
            if (Array.isArray(reading.measurement)) {
                if (Array.isArray(reading.measurement[0])) {
                    let averageVals = (reading.measurement.reduce((total, item) => total + item[0], 0))/reading.measurement.length;
                    measurementVal = averageVals;
                } else {
                    let averageVals = (reading.measurement.reduce((total, item) => total + item, 0))/reading.measurement.length;
                    measurementVal = averageVals;
                }
            }
            if (measurementVal?.pNN50 != null) {
                measurementVal = measurementVal.pNN50;
            }
            if (typeof reading.measurement === 'string') {
                let floatVal = parseFloat(reading.measurement);
                if (!isNaN(floatVal)) {
                    measurementVal = parseFloat(floatVal.toFixed(1));
                }
            } else if (typeof reading.measurement === 'number') {
                measurementVal = parseFloat(measurementVal.toFixed(1));
            }
            switch (reading.modalityId) {
                case 1:
                    const convertedTempMeasurement = convertTemperature(measurementVal, unitType);
                    deviceGroup.measurements.temperature = convertedTempMeasurement;
                    if (groupedByAvg && reading.groupMaxVal) {
                        const convertedMax = convertTemperature(reading.groupMaxVal, unitType);
                        const convertedMin = convertTemperature(reading.groupMinVal, unitType);
                        deviceGroup.measurements['temperature-range'] = {
                            "max": parseFloat(convertedMax.toFixed(1)), 
                            "min": parseFloat(convertedMin.toFixed(1))
                        };
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.temperature = null;
                        }
                        deviceGroup.measurements.temperature_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.temperature_adhoc = true;
                    }
                    break;
                case 2:
                    deviceGroup.measurements.heartRate = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['heartRate-range'] = {
                            "max": parseFloat(reading.groupMaxVal.toFixed(1)), 
                            "min": parseFloat(reading.groupMinVal.toFixed(1))
                        };
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.heartRate = null;
                        }
                        deviceGroup.measurements.heartRate_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.heartRate_adhoc = true;
                    }
                    break;
                case 3:
                    deviceGroup.measurements.oxygenSaturation = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['oxygenSaturation-range'] = {
                            "max": parseFloat(reading.groupMaxVal.toFixed(1)), 
                            "min": parseFloat(reading.groupMinVal.toFixed(1))
                        };
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.oxygenSaturation = null;
                        }
                        deviceGroup.measurements.oxygenSaturation_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.oxygenSaturation_adhoc = true;
                    }
                    break;
                case 4:
                    deviceGroup.measurements.ECG = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['ECG-range'] = {
                            "max": parseFloat(reading.groupMaxVal.toFixed(1)), 
                            "min": parseFloat(reading.groupMinVal.toFixed(1))
                        };
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.ECG = null;
                        }
                        deviceGroup.measurements.ECG_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.ECG_adhoc = true;
                    }
                    break;
                case 5:
                    deviceGroup.measurements.systolic = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['systolic-range'] = {
                            "max": parseFloat(reading.groupMaxVal.toFixed(1)), 
                            "min": parseFloat(reading.groupMinVal.toFixed(1))
                        };
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.systolic = null;
                        }
                        deviceGroup.measurements.systolic_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.systolic_adhoc = true;
                    }
                    break;
                case 6:
                    deviceGroup.measurements.respiratoryRate = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['respiratoryRate-range'] = {
                            "max": parseFloat(reading.groupMaxVal.toFixed(1)), 
                            "min": parseFloat(reading.groupMinVal.toFixed(1))
                        };
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.respiratoryRate = null;
                        }
                        deviceGroup.measurements.respiratoryRate_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.respiratoryRate_adhoc = true;
                    }
                    break;
                case 7:
                    if (typeof reading.measurement === 'string') {
                        deviceGroup.measurements.AUSC = 0;
                    } else {
                        deviceGroup.measurements.AUSC = measurementVal;
                    }
                    break;
                case 8:
                    const convertedWeightMeasurement = convertWeight(measurementVal, unitType);
                    deviceGroup.measurements.weight = convertedWeightMeasurement;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        const convertedMax = convertWeight(reading.groupMaxVal, unitType);
                        const convertedMin = convertWeight(reading.groupMinVal, unitType);
                        deviceGroup.measurements['weight-range'] = {
                            "max": parseFloat(convertedMax.toFixed(1)), 
                            "min": parseFloat(convertedMin.toFixed(1))
                        };
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.weight_adhoc = true;
                    }
                    break;
                case 10:
                    deviceGroup.measurements.diastolic = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['diastolic-range'] = {"max":parseFloat(reading.groupMaxVal.toFixed(1)), "min": parseFloat(reading.groupMinVal.toFixed(1))};
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.diastolic = null;
                        }
                        deviceGroup.measurements.diastolic_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.diastolic_adhoc = true;
                    }
                    break;
                case 11:
                    deviceGroup.measurements.glucoseLevel = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['glucoseLevel-range'] = {"max":parseFloat(reading.groupMaxVal.toFixed(1)), "min": parseFloat(reading.groupMinVal.toFixed(1))};
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.glucoseLevel_adhoc = true;
                    }
                    break;
                case 13:
                    deviceGroup.measurements.steps = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['steps-range'] = {"max":parseFloat(reading.groupMaxVal.toFixed(1)), "min": parseFloat(reading.groupMinVal.toFixed(1))};
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.steps = null;
                        }
                        deviceGroup.measurements.steps_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.steps_adhoc = true;
                    }
                    break;
                case 15:
                    deviceGroup.measurements.ECGHRV = measurementVal;
                    if (groupedByAvg && reading.groupMaxVal != null) {
                        deviceGroup.measurements['ECGHRV-range'] = {"max":parseFloat(reading.groupMaxVal.toFixed(1)), "min": parseFloat(reading.groupMinVal.toFixed(1))};
                    }
                    if (reading.SQI_reading_accuracy) {
                        if (reading.SQI_reading_accuracy === "unacceptable") {
                            deviceGroup.measurements.ECGHRV = null;
                        }
                        deviceGroup.measurements.ECGHRV_sqi = reading.SQI_reading_accuracy;
                    }
                    if (reading.scheduledReading === false) {
                        deviceGroup.measurements.ECGHRV_adhoc = true;
                    }
                    break;
                case 18:
                    if (reading.modalityName === 'Posture') {
                        deviceGroup.measurements.posture = measurementVal;
                    } else if (reading.modalityName === 'Activity Level') {
                        deviceGroup.measurements.activityLevel = measurementVal;
                    }
                    break;
                default:
                    break;
            }
        });

        let auscGroupedData = {};
        const sortedAuscData = [...auscData].sort((a, b) => {
            return new Date(a.completedDate) - new Date(b.completedDate);
        });

        sortedAuscData.forEach(reading => {
            const dateString = reading.completedDate;
            const completedDate = new Date(dateString);
            const formattedDate = formatDateStringWithoutYear(completedDate.toISOString(), dateFormat);
            const hour = String(completedDate.getHours()).padStart(2, '0');
            const minute = String(completedDate.getMinutes()).padStart(2, '0');
            const second = String(completedDate.getSeconds()).padStart(2, '0');
            const groupKey = `${formattedDate}-${hour}:${minute}:${second}`;
            const dateKey = `${formattedDate}-${hour}:${minute}`;
            const displayKey = formattedDate;

            if (!auscGroupedData[groupKey]) {
                auscGroupedData[groupKey] = {
                    displayKey,
                    dateKey,
                    timeStamp: dateString,
                    formattedDate,
                    sortTime: completedDate.getTime(),
                    data: []
                };
            }

            let deviceGroup = auscGroupedData[groupKey].data.find(group => group.deviceId === reading.deviceId);
            if (!deviceGroup) {
                deviceGroup = {
                    deviceId: reading.deviceId,
                    deviceName: reading.model,
                    measurement: null
                };
                auscGroupedData[groupKey].data.push(deviceGroup);
            }

            let measurementVal = reading.measurement;
            if (typeof reading.measurement === 'string') {
                measurementVal = 0;
            }
            deviceGroup.measurement = measurementVal;
        });

        const transformedData = [];
        const transformedAuscData = [];

        const sortedKeys = Object.keys(groupedData).sort((a, b) => {
            return groupedData[a].sortTime - groupedData[b].sortTime;
        });

        sortedKeys.forEach(date => {
            groupedData[date].data.forEach(deviceGroup => {
                const tooltipDate = new Date(groupedData[date].timeStamp);
                const formattedTooltipDate = formatDateString(tooltipDate.toISOString(), dateFormat);
                const formattedTime = tooltipDate.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true
                });

                transformedData.push({
                    date: groupedData[date].dateKey,
                    displayDate: groupedData[date].displayKey,
                    formattedDate: groupedData[date].formattedDate,
                    tooltipDate: `${formattedTooltipDate} ${formattedTime}`,
                    timeStamp: groupedData[date].timeStamp,
                    sortTime: groupedData[date].sortTime,
                    deviceId: deviceGroup.deviceId,
                    deviceName: deviceGroup.deviceName,
                    ...deviceGroup.measurements
                });
            });
        });

        const sortedAuscKeys = Object.keys(auscGroupedData).sort((a, b) => {
            return auscGroupedData[a].sortTime - auscGroupedData[b].sortTime;
        });

        sortedAuscKeys.forEach(date => {
            auscGroupedData[date].data.forEach(deviceGroup => {
                const tooltipDate = new Date(auscGroupedData[date].timeStamp);
                const formattedTooltipDate = formatDateString(tooltipDate.toISOString(), dateFormat);
                const formattedTime = tooltipDate.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true
                });

                transformedAuscData.push({
                    date: auscGroupedData[date].dateKey,
                    displayDate: auscGroupedData[date].displayKey,
                    formattedDate: auscGroupedData[date].formattedDate,
                    tooltipDate: `${formattedTooltipDate} ${formattedTime}`,
                    timeStamp: auscGroupedData[date].timeStamp,
                    sortTime: auscGroupedData[date].sortTime,
                    deviceId: deviceGroup.deviceId,
                    deviceName: deviceGroup.deviceName,
                    measurement: deviceGroup.measurement
                });
            });
        });

        return { transformedData, transformedAuscData };
    }, [dateFormat, readingInterval, unitType]);

    // Extract fetchData to useCallback for reuse
    const fetchData = useCallback(async () => {
            try {
                const response = await httpService.get(`device/readings/${id}?startDate=${startDate}&endDate=${endDate}&interval=${readingInterval}&calibrationReading=false`);
                const alertsResponse = await httpService.get(`alerts/${id}?startDate=${startDate}&endDate=${endDate}`);
                if (Array.isArray(alertsResponse.data.data)) {
                    setAlertsForPeriod(alertsResponse.data.data)
                }
                const fetchedData = response.data.data;

                const filteredData = fetchedData.filter(reading => reading.modalityId !== 7);
                let groupedByAvg = false;
                if (filteredData.length >= 1) {
                    groupedByAvg = fetchedData[0].averageGrouped;
                    dispatch(setAverageGrouped(groupedByAvg));
                }

                const { transformedData, transformedAuscData } = processReadingsIntoGraphFormat(fetchedData, groupedByAvg);
                setAuscData(transformedAuscData);
                setHealthEvaluationData(transformedData);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        }, [startDate, endDate, httpService, id, readingInterval, dispatch, processReadingsIntoGraphFormat]);

    // Fetch data when filters change
    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id, fetchData]);

    // Update graphs seamlessly from WebSocket readings (no API call)
    useEffect(() => {
        if (!lastUpdateTimestamp || !id || !startDate || !endDate || !processReadingsIntoGraphFormat) {
            return;
        }
        if (!deviceReadings || deviceReadings.length === 0) {
            return;
        }
        // Extract readings for current patient
        const patientReadings = [];
        deviceReadings.forEach(item => {
            if (item.readings && Array.isArray(item.readings)) {
                if (item.accountId === id) {
                    patientReadings.push(...item.readings);
                }
            } else if (item.accountId === id && item.completedDate) {
                patientReadings.push(item);
            }
        });
        if (patientReadings.length === 0) {
            return;
        }
        // Filter readings within date range
        const readingsInRange = patientReadings.filter(reading => {
            if (!reading.completedDate) return false;
            const readingDate = new Date(reading.completedDate);
            return readingDate >= new Date(startDate) && readingDate <= new Date(endDate);
        });
        if (readingsInRange.length === 0) {
            return;
        }
        // Process and merge new readings
        const { transformedData: newData, transformedAuscData: newAuscData } = processReadingsIntoGraphFormat(readingsInRange, averageGrouped);
        setHealthEvaluationData(prevData => {
            const dataMap = new Map();
            prevData.forEach(item => dataMap.set(`${item.timeStamp}-${item.deviceId}`, item));
            newData.forEach(item => dataMap.set(`${item.timeStamp}-${item.deviceId}`, item));
            return Array.from(dataMap.values()).sort((a, b) => a.sortTime - b.sortTime);
        });
        setAuscData(prevAuscData => {
            const dataMap = new Map();
            prevAuscData.forEach(item => dataMap.set(`${item.timeStamp}-${item.deviceId}`, item));
            newAuscData.forEach(item => dataMap.set(`${item.timeStamp}-${item.deviceId}`, item));
            return Array.from(dataMap.values()).sort((a, b) => a.sortTime - b.sortTime);
        });
    }, [deviceReadings, lastUpdateTimestamp, id, startDate, endDate, processReadingsIntoGraphFormat, averageGrouped]);

    useEffect(() => {
        dispatch(resetVitalState());
        // Get all available chart values from chartOptions
        const allChartValues = carePrograms.reduce((chartValues, item) => {
            if (item.patient.id === id) {
                item.devices.forEach(device => {
                    switch (device.modalityId) {
                        case 3:
                            chartValues.push('oxygenSaturation');
                            dispatch(setItem({ key: "isOxygenSaturation", value: true }));
                            dispatch(toggleShowingTrue("oxygenSaturation"));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            break;
                        case 4:
                            chartValues.push('ECG');
                            dispatch(setItem({ key: "isECG", value: true }));
                            dispatch(toggleShowingTrue("ECG"));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            break;
                        case 15:
                            chartValues.push('ECGHRV');
                            dispatch(setItem({ key: "isECGHRV", value: true }));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            dispatch(toggleShowingTrue("ECGHRV"));
                            break;
                        case 5:
                            chartValues.push('bloodPressure');
                            dispatch(setItem({ key: "isBloodPressure", value: true }));
                            dispatch(setItem({ key: "bloodPressureDeviceName", value: device.modalityName }));
                            dispatch(setModality({ 
                                modalityId: device.modalityId, 
                                updates: {
                                    ...device,
                                    systolicUpperLimit: device.upperLimit,
                                    systolicLowerLimit: device.lowerLimit
                                }
                            }));
                            dispatch(toggleShowingTrue("bloodPressure"));
                            break;
                        case 1:
                            chartValues.push('temperature');
                            dispatch(setItem({ key: "isTemperature", value: true }));
                            dispatch(toggleShowingTrue("temperature"));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            break;
                        case 6:
                            chartValues.push('respiratoryRate');
                            dispatch(setItem({ key: "isRespiratoryRate", value: true }));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            dispatch(toggleShowingTrue("respiratoryRate"));
                            break;
                        case 10:
                            dispatch(setModality({ 
                                modalityId: 5, 
                                updates: {
                                    diastolicUpperLimit: device.upperLimit,
                                    diastolicLowerLimit: device.lowerLimit
                                }
                            }));
                            break;
                        case 8:
                            chartValues.push('weight');
                            dispatch(setItem({ key: "isWeight", value: true }));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            dispatch(toggleShowingTrue("weight"));
                            break;
                        case 7:
                            chartValues.push('AUSC');
                            dispatch(setItem({ key: "isAUSC", value: true }));
                            dispatch(setItem({ key: "auscDeviceName", value: device.modalityName }));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            dispatch(toggleShowingTrue("AUSC"));
                            break;
                        case 13:
                            chartValues.push('steps');
                            dispatch(setItem({ key: "isSteps", value: true }));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            dispatch(toggleShowingTrue("steps"));
                            break;
                        case 11:
                            chartValues.push('glucoseLevel');
                            dispatch(setItem({ key: "isGlucoseLevel", value: true }));
                            dispatch(setModality({ modalityId: device.modalityId, updates: device }));
                            dispatch(toggleShowingTrue("glucoseLevel"));
                            break;
                        default:
                            break;
                    }
                });
            }
            return chartValues;
        }, []);

        // Set all available charts as selected
        let uniqueCharts = new Set(allChartValues);
        dispatch(setSelectedCharts(Array.from(uniqueCharts)));
    }, [carePrograms, dispatch, id]);

    const chartComponents = {
        oxygenSaturation: {
            component: OxygenSaturationCardView,
            props: { 
                vitalName: 'oxygenSaturation', 
                vitalUnit: '%', 
                handlePointClick,
                alertsForPeriod
            }
        },
        ECG: {
            component: ECGCardView,
            props: { 
                vitalName: 'ECG', 
                vitalUnit: ' BPM',
                handlePointClick,
                alertsForPeriod
            }
        },
        ECGHRV: {
            component: ECGHRVCardView,
            props: { 
                vitalName: 'ECGHRV', 
                vitalUnit: '%',
                handlePointClick,
                alertsForPeriod
            }
        },
        bloodPressure: {
            component: BloodPressureCardView,
            props: { 
                vitalName: 'bloodPressure', 
                vitalUnit: ' mmHg',
                handlePointClick,
                alertsForPeriod
            }
        },
        temperature: {
            component: TemperatureCardView,
            props: { 
                vitalName: 'temperature', 
                vitalUnit: unitType === 'Imperial' ? '°F' : '°C',
                handlePointClick,
                alertsForPeriod
            }
        },
        respiratoryRate: {
            component: RespirationCardView,
            props: { 
                vitalName: 'respiratoryRate', 
                vitalUnit: ' BPM', 
                handlePointClick,
                alertsForPeriod
            }
        },
        weight: {
            component: WeightCardView,
            props: { 
                vitalName: 'weight', 
                vitalUnit: unitType === 'Imperial' ? ' lb' : ' kg', 
                handlePointClick,
                alertsForPeriod
            }
        },
        AUSC: {
            component: AuscultationCardView,
            props: { 
                vitalName: 'AUSC', 
                vitalUnit: ' Hz',
                auscData: auscData,
                handlePointClick,
                alertsForPeriod
            }
        },
        steps: {
            component: StepCardView,
            props: { 
                vitalName: 'steps', 
                vitalUnit: ' Steps', 
                handlePointClick,
                alertsForPeriod
            }
        },
        glucoseLevel: {
            component: GlucoseLevelCardView,
            props: { 
                vitalName: 'glucoseLevel', 
                vitalUnit: ' mg/dL',
                handlePointClick,
                alertsForPeriod
            }
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const reorderedCharts = Array.from(vitalChartOrder);
        const [reorderedChart] = reorderedCharts.splice(result.source.index, 1);
        reorderedCharts.splice(result.destination.index, 0, reorderedChart);

        setVitalChartOrder(reorderedCharts);
    };

    const vitalsView = () => {
        const getLatestTimestamp = () => {
            if (!healthEvaluationData || healthEvaluationData.length === 0) return null;
            
            // Filter data for only selected charts
            const selectedData = healthEvaluationData.filter(reading => {
                return selectedCharts.some(chartName => {
                    const measurement = reading[chartName];
                    return measurement !== null && measurement !== undefined;
                });
            });

            if (selectedData.length === 0) return null;

            // Find the latest timestamp
            const latestReading = selectedData.reduce((latest, current) => {
                const currentDate = new Date(current.timeStamp);
                const latestDate = new Date(latest.timeStamp);
                return currentDate > latestDate ? current : latest;
            });

            // Format the timestamp using formatDateString
            const date = new Date(latestReading.timeStamp);
            const formattedDate = formatDateString(date.toISOString(), dateFormat);
            const formattedTime = date.toLocaleString('en-US', {
                hour: 'numeric',
                minute: 'numeric'
            });
            return `${formattedDate} ${formattedTime}`;
        };

        const latestTimestamp = getLatestTimestamp();

        return (
            <div className={styles.ViewContainer}>
                <div style={{width: "100%", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                    <FilterPatientView isTimePeriod={true} expand={true} timestamp={latestTimestamp}/>
                    <div onClick={()=>dispatch(toggleVitalsExpanded())}>
                        <ExpandIcon />
                    </div>    
                </div>
                <div className={vitalsExpanded ? "" : styles.PatientViewScrollContainer}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="vitalCharts">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className={styles.scrollableContainer}>
                                {vitalChartOrder.map((chartName, index) => {
                                    const chartInfo = chartComponents[chartName];
                                    if (!chartRefs.current[chartName]) {
                                        chartRefs.current[chartName] = React.createRef();
                                    }
                                    if (vitals[`is${chartName.charAt(0).toUpperCase() + chartName.slice(1)}`] && selectedCharts?.includes(chartName)) {
                                        return (
                                            <Draggable key={chartName} draggableId={chartName} index={index}>
                                                {(provided) => (
                                                   <div
                                                   ref={provided.innerRef}
                                                   {...provided.draggableProps}
                                                   {...provided.dragHandleProps}
                                                   className={styles.child}
                                               >
                                                   {React.createElement(chartInfo.component, {
                                                       ...chartInfo.props,
                                                       className: styles.child,
                                                       healthEvaluationData: healthEvaluationData,
                                                       ref: chartRefs.current[chartName]
                                                    })}
                                                </div> 
                                                )}
                                            </Draggable>
                                        );
                                    }
                                    return null;
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                </div>
                <ChartDetailView
                    isModalVisible={isModalOpen}
                    customModal={styles.customModal}
                    handleCloseModal={() => setIsModalOpen(false)}
                    modalData={modalContent}
                />
            </div>
        );
    };

    return (
        vitalsExpanded ? 
            <Modal
            show={vitalsExpanded}
            onHide={() => dispatch(toggleVitalsExpanded())}
            size="xl"
            aria-labelledby="vitals-modal-title"
            centered
            dialogClassName="vitals-modal"
        >
            <Modal.Body style={{overflowY: 'auto'}}>
                {vitalsView()}
            </Modal.Body>
        </Modal>
        :
        vitalsView()
    );
};

export default VitalsView;
